import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  IconButton,
  Paper,
  Stack,
  keyframes,
  styled,
  CircularProgress,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { useNavigate } from "react-router-dom";
import { galleryImages } from '../images';
import { RsvpText, RsvpInput  } from '../theme';
import useImagePreloader from "../hooks/useImagePreloader";

// Import corner images
import topleft from '../images/rsvp/topleft.png';
import topright from '../images/rsvp/topright.png';
import botleft from '../images/rsvp/botleft.png';
import botright from '../images/rsvp/botright.png';
import bg from '../images/rsvp/white-paper-texture-crop.jpg';

const fadein = () => keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const AnimatedPaper = styled(Paper)`
  animation: ${fadein} 2s ease-out;
`;

const CustomTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    marginBottom: "10px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(54, 82, 60, 0.5)", // Updated to match new color
      borderWidth: "2px",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(54, 82, 60, 1)",
      borderWidth: "2px",
    },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(180, 95, 95, 0.8)", // Keep error color
    },
    "& input": {
      color: "rgba(54, 82, 60, 1)", // Input text color
    },
  },
  "& .MuiFormLabel-root": {
    color: "rgba(54, 82, 60, 0.8)", // Label color
    "&.Mui-focused": {
      color: "rgba(54, 82, 60, 1)",
    },
  },
  "& .MuiFormHelperText-root.Mui-error": {
    position: "absolute",
    top: "84%",
    color: "rgba(180, 95, 95, 0.8)",
  },
  "& .MuiFormLabel-root.Mui-error": {
    color: "rgba(180, 95, 95, 0.8)",
  },
});

const minHeight = '900px'; 

function RSVP() {
  useImagePreloader('gallery');

  const [mainGuest, setMainGuest] = useState({ name: "", email: "" });
  const [additionalGuests, setAdditionalGuests] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Joseph & Achsa | RSVP";
  }, []);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleMainGuestChange = (e) => {
    setMainGuest({ ...mainGuest, [e.target.name]: e.target.value });
  };

  const handleAdditionalGuestChange = (index, value) => {
    const newGuests = [...additionalGuests];
    newGuests[index].name = value;
    setAdditionalGuests(newGuests);
  };

  const addGuest = () => {
    setAdditionalGuests([...additionalGuests, { name: "" }]);
  };

  const removeGuest = (index) => {
    const newGuests = additionalGuests.filter((_, i) => i !== index);
    setAdditionalGuests(newGuests);
  };

  const submitRsvp = async () => {
    if (!mainGuest.name.trim() || !isValidEmail(mainGuest.email)) {
      setSubmitted(true);
      return;
    }

    try {
      setIsLoading(true);
      const rsvpData = {
        mainGuest: {
          name: mainGuest.name.trim(),
          email: mainGuest.email.trim(),
        },
        additionalGuests: additionalGuests.filter(
          (guest) => guest.name.trim() !== ""
        ),
        timestamp: new Date(),
      };

      await addDoc(collection(db, "rsvps"), rsvpData);

      // Clear form
      setMainGuest({ name: "", email: "" });
      setAdditionalGuests([]);

      // Navigate to gallery
      navigate("/gallery");
    } catch (error) {
      console.error("Error submitting RSVP:", error);
      // You could add error handling here
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: {
          xs: "auto", // Enable scrolling on mobile
          sm: "hidden", // Prevent parent scrolling on desktop
        },
        [`@media (max-height: ${minHeight})`]: {
          overflow: "auto",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: {
            xs: "none",
            sm: `url(${galleryImages.IMG_9958})`,
          },
          [`@media (max-height: ${minHeight})`]: {
            backgroundImage: "none",
          },
          backgroundSize: "cover",
          backgroundPosition: "center",
          zIndex: -1,
        }}
      />

      <AnimatedPaper
        sx={{
          position: {
            xs: "absolute", // Position absolutely on mobile
            sm: "relative", // Normal positioning on desktop
          },
          top: 0,
          left: 0,
          width: {
            xs: "100%", // Full width on mobile
            sm: "90%", // 90% width on desktop
          },
          maxWidth: {
            xs: "100%",
            sm: "600px",
          },
          maxHeight: {
            xs: "unset", // Remove max height constraint on mobile
            sm: "1000px",
          },
          height: {
            xs: "auto", // Let it grow with content on mobile
            sm: "90vh",
          },
          background: "rgba(255, 255, 255, 1)",
          borderRadius: {
            xs: 0,
            sm: "14px",
          },
          minHeight: {
            xs: "100%", // At least full height on mobile
            sm: "auto",
          },
          display: "flex",
          zIndex: 1,
          overflow: "hidden",
          [`@media (max-height: ${minHeight})`]: {
            maxHeight: "unset",
            height: "unset",
            minHeight: "100%",
            maxWidth: "100%",
            width: "100%",
            position: "absolute",
            borderRadius: "0px",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${bg})`,

            opacity: 0.25, // Added opacity for background image
          }}
        />
        <Box
          sx={{
            position: "relative",
            border: "3px solid #36523c",
            borderRadius: "8px",
            padding: {
              xs: "16px",
              sm: "28px",
            },
            margin: "8px",
            flex: 1,
            // overflowY: {
            //   xs: "visible",
            //   sm: "auto",
            // },
            overflow: "hidden", 
            display: "flex",
            flexDirection: "column",
            gap: 0,
            justifyContent: "flex-start",
          }}
        >
          {/* Corner Images */}
          <Box
            component="img"
            src={topleft}
            sx={{
              position: "absolute",
              top: "-3px",
              left: "-3px",
              width: "120px",
              height: "auto",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
          <Box
            component="img"
            src={topright}
            sx={{
              position: "absolute",
              top: "-3px",
              right: "-3px",
              width: "120px",
              height: "auto",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
          <Box
            component="img"
            src={botleft}
            sx={{
              position: "absolute",
              bottom: "-3px",
              left: "-3px",
              width: "120px",
              height: "auto",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
          <Box
            component="img"
            src={botright}
            sx={{
              position: "absolute",
              bottom: "-3px",
              right: "-3px",
              width: "120px",
              height: "auto",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />

          {/* RSVP Header */}
          <Box>
            <RsvpText sx={{ mb: 2, textAlign: "center", fontSize: "1.8rem" }}>
              RSVP
            </RsvpText>
            <RsvpText sx={{ textAlign: "center", fontSize: "1.2rem" }}>
              We can't wait to celebrate with you!
            </RsvpText>
            <RsvpText sx={{ mb: 4, textAlign: "center", fontSize: "1.2rem" }}>
              <br />
              21st June 2025 - 1:15pm
              <br />
              42 Settlement Road, Mount Mee, QLD 4521
              <br />
              Please RSVP before the 30th April 2025
            </RsvpText>
          </Box>

          {/* Your Details */}
          <Box>
            <RsvpInput sx={{ mb: 2, fontSize: "1.2rem" }}>
              Your Details
            </RsvpInput>
            <Stack spacing={2}>
              <CustomTextField
                fullWidth
                label="Full Name"
                name="name"
                color="primary"
                disabled={isLoading}
                value={mainGuest.name}
                onChange={handleMainGuestChange}
                variant="outlined"
                error={submitted && mainGuest.name.trim() === ""}
                helperText={
                  submitted && mainGuest.name.trim() === ""
                    ? "Name is required"
                    : ""
                }
              />
              <CustomTextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                color="primary"
                disabled={isLoading}
                value={mainGuest.email}
                onChange={handleMainGuestChange}
                variant="outlined"
                error={
                  submitted &&
                  (!mainGuest.email.trim() || !isValidEmail(mainGuest.email))
                }
                helperText={
                  submitted && !mainGuest.email.trim()
                    ? "Email is required"
                    : submitted && !isValidEmail(mainGuest.email)
                    ? "Please enter a valid email"
                    : ""
                }
              />
            </Stack>
          </Box>

          {/* Additional Guests */}
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>

          {additionalGuests.length > 0 && (
            <>
              <RsvpInput sx={{ mb: 1, mt: 3, fontSize: "1.2rem" }}>
                Additional Guests
              </RsvpInput>
              <Box
                sx={{
                  position: "relative",
                  flex: 1,
                  maxHeight: {
                    xs: "none", 
                    sm: "calc(100vh - 600px)", // Fixed height for scrollable area
                  },
                  minHeight: {
                    xs: "none",
                    sm: "100px", // Ensure there's always some visible space
                  },
                  overflowY: {
                    xs: "visible",
                    sm: "auto",
                  },
                  paddingRight: {
                    xs: 0,
                    sm: "12px",
                  },
                  marginRight: {
                    xs: 0,
                    sm: "-12px", // Compensate for padding when no scrollbar
                  },
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(0, 0, 0, 0.2)",
                    borderRadius: "10px",
                    "&:hover": {
                      background: "rgba(0, 0, 0, 0.3)",
                    },
                  },
                  [`@media (max-height: ${minHeight})`]: {
                    maxHeight: "none",
                    minHeight: "none",
                    overflowY: "visible",
                  },
                }}
              >
                <Stack
                  spacing={2}
                  sx={{ marginBottom: "10px", marginTop: "10px" }}
                >
                  {additionalGuests.map((guest, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <CustomTextField
                        fullWidth
                        label={`Guest ${index + 1} Name`}
                        value={guest.name}
                        onChange={(e) =>
                          handleAdditionalGuestChange(index, e.target.value)
                        }
                        variant="outlined"
                        disabled={isLoading}
                        error={submitted && guest.name.trim() === ""}
                        helperText={
                          submitted && guest.name.trim() === ""
                            ? "Name is required"
                            : ""
                        }
                        color="primary"
                        slotProps={{
                          input: {
                            endAdornment: (
                              <IconButton
                                onClick={() => removeGuest(index)}
                                color="rgb(34, 21, 4)"
                              >
                                <RemoveCircleOutlineIcon />
                              </IconButton>
                            ),
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
              </>
          )}
            </Box>

          <Stack
            spacing={2}
            sx={{
              mt: 3,
            }}
          >
            <Button
              startIcon={<AddCircleOutlineIcon />}
              onClick={addGuest}
              disabled={isLoading}
              sx={{
                fontFamily: 'serif',
                background: "rgba(232, 227, 207, 0.2)",
                backdropFilter: "blur(2px)",
                zIndex: 2,
                border: "2px solid #36523c",
                color: "#36523c",
                padding: "0.5rem 3rem",
                fontSize: "1.1rem",
                letterSpacing: "0.1rem",
                borderRadius: "8px",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.8)",
                },
                "&:active": {
                  transform: "translateY(1px)",
                },
              }}
            >
              Add Guest
            </Button>

            <Button
              onClick={submitRsvp}
              disabled={isLoading}
              sx={{
                mt: 4,
                fontFamily: 'serif',
                background: "rgba(232, 227, 207, 0.2)",
                backdropFilter: "blur(2px)",
                zIndex: 2,
                border: "2px solid #36523c",
                color: "#36523c",
                padding: "0.5rem 3rem",                
                fontSize: "1.1rem",
                letterSpacing: "0.1rem",
                borderRadius: "8px",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.8)",
                },
                "&:active": {
                  transform: "translateY(1px)",
                },
                position: "relative",
              }}
            >
              {isLoading ? (
                <CircularProgress
                  size={24}
                  sx={{
                    color: "#36523c",
                    position: "relative",
                    marginTop: "7px",
                    marginBottom: "7px",
                    marginLeft: "4px",
                  }}
                />
              ) : (
                "Submit RSVP"
              )}
            </Button>
          </Stack>
        </Box>
      </AnimatedPaper>
    </Box>
  );
}

export default RSVP;
